import { ConnectorType, FieldMapping } from '../types';

export interface FieldDefinition {
  field: string;
  labelKey: string;
  required?: boolean;
  isArray?: boolean;
}

export const ARDEALY_TARGET_FIELDS: FieldDefinition[] = [
  { field: 'name', labelKey: 'PAGES.API_INTEGRATION.TARGET_FIELDS.NAME' },
  { field: 'sku', labelKey: 'PAGES.API_INTEGRATION.TARGET_FIELDS.SKU', required: true },
  { field: 'price', labelKey: 'PAGES.API_INTEGRATION.TARGET_FIELDS.PRICE', required: true },
  { field: 'availability', labelKey: 'PAGES.API_INTEGRATION.TARGET_FIELDS.AVAILABILITY', required: true },
  { field: 'stock_quantity', labelKey: 'PAGES.API_INTEGRATION.TARGET_FIELDS.STOCK_QUANTITY' },
  { field: 'description', labelKey: 'PAGES.API_INTEGRATION.TARGET_FIELDS.DESCRIPTION' },
  { field: 'category', labelKey: 'PAGES.API_INTEGRATION.TARGET_FIELDS.CATEGORY' },
  { field: 'gallery_images', labelKey: 'PAGES.API_INTEGRATION.TARGET_FIELDS.GALLERY_IMAGES', isArray: true },
];

export const SOURCE_FIELD_SCHEMAS: Record<ConnectorType, FieldDefinition[]> = {
  shopify: [
    { field: 'title', labelKey: 'title' },
    { field: 'variants.sku', labelKey: 'variants.sku' },
    { field: 'variants.price', labelKey: 'variants.price' },
    { field: 'status', labelKey: 'status' },
    { field: 'variants.inventory_quantity', labelKey: 'variants.inventory_quantity' },
    { field: 'body_html', labelKey: 'body_html' },
    { field: 'product_type', labelKey: 'product_type' },
    { field: 'images[0].src', labelKey: 'images[0].src' },
    { field: 'images[1].src', labelKey: 'images[1].src' },
    { field: 'images[2].src', labelKey: 'images[2].src' },
    { field: 'vendor', labelKey: 'vendor' },
  ],
  woocommerce: [
    { field: 'name', labelKey: 'name' },
    { field: 'sku', labelKey: 'sku' },
    { field: 'price', labelKey: 'price' },
    { field: 'status', labelKey: 'status' },
    { field: 'stock_quantity', labelKey: 'stock_quantity' },
    { field: 'description', labelKey: 'description' },
    { field: 'categories[0].name', labelKey: 'categories[0].name' },
    { field: 'images[0].src', labelKey: 'images[0].src' },
    { field: 'images[1].src', labelKey: 'images[1].src' },
    { field: 'images[2].src', labelKey: 'images[2].src' },
  ],
  prestashop: [
    { field: 'name', labelKey: 'name' },
    { field: 'reference', labelKey: 'reference' },
    { field: 'price', labelKey: 'price' },
    { field: 'active', labelKey: 'active' },
    { field: 'quantity', labelKey: 'quantity' },
    { field: 'description', labelKey: 'description' },
    { field: 'id_category_default', labelKey: 'id_category_default' },
    { field: 'images[0].src', labelKey: 'images[0].src' },
    { field: 'images[1].src', labelKey: 'images[1].src' },
    { field: 'images[2].src', labelKey: 'images[2].src' },
  ],
  magento: [
    { field: 'name', labelKey: 'name' },
    { field: 'sku', labelKey: 'sku' },
    { field: 'price', labelKey: 'price' },
    { field: 'status', labelKey: 'status' },
    { field: 'extension_attributes.stock_item.qty', labelKey: 'extension_attributes.stock_item.qty' },
    { field: 'custom_attributes.description', labelKey: 'custom_attributes.description' },
    { field: 'custom_attributes.category_ids', labelKey: 'custom_attributes.category_ids' },
    { field: 'media_gallery_entries[0].file', labelKey: 'media_gallery_entries[0].file' },
  ],
  drupal: [
    { field: 'title', labelKey: 'title' },
    { field: 'field_sku', labelKey: 'field_sku' },
    { field: 'field_price', labelKey: 'field_price' },
    { field: 'status', labelKey: 'status' },
    { field: 'field_stock', labelKey: 'field_stock' },
    { field: 'body.value', labelKey: 'body.value' },
    { field: 'field_category', labelKey: 'field_category' },
    { field: 'field_image', labelKey: 'field_image' },
  ],
  simple_db: [
    { field: 'name', labelKey: 'name' },
    { field: 'sku', labelKey: 'sku' },
    { field: 'price', labelKey: 'price' },
    { field: 'available', labelKey: 'available' },
    { field: 'stock', labelKey: 'stock' },
    { field: 'description', labelKey: 'description' },
    { field: 'category', labelKey: 'category' },
    { field: 'image', labelKey: 'image' },
  ],
};

let mappingIdCounter = 0;
const createMapping = (source: string, target: string): FieldMapping => ({
  id: `auto-${++mappingIdCounter}`,
  sourceField: source,
  targetField: target,
});

export const AUTO_MAPPING_PRESETS: Record<ConnectorType, FieldMapping[]> = {
  shopify: [
    createMapping('title', 'name'),
    createMapping('variants.sku', 'sku'),
    createMapping('variants.price', 'price'),
    createMapping('status', 'availability'),
    createMapping('variants.inventory_quantity', 'stock_quantity'),
    createMapping('body_html', 'description'),
    createMapping('product_type', 'category'),
  ],
  woocommerce: [
    createMapping('name', 'name'),
    createMapping('sku', 'sku'),
    createMapping('price', 'price'),
    createMapping('status', 'availability'),
    createMapping('stock_quantity', 'stock_quantity'),
    createMapping('description', 'description'),
    createMapping('categories[0].name', 'category'),
  ],
  prestashop: [
    createMapping('name', 'name'),
    createMapping('reference', 'sku'),
    createMapping('price', 'price'),
    createMapping('active', 'availability'),
    createMapping('quantity', 'stock_quantity'),
    createMapping('description', 'description'),
  ],
  magento: [
    createMapping('name', 'name'),
    createMapping('sku', 'sku'),
    createMapping('price', 'price'),
    createMapping('status', 'availability'),
    createMapping('extension_attributes.stock_item.qty', 'stock_quantity'),
    createMapping('custom_attributes.description', 'description'),
  ],
  drupal: [
    createMapping('title', 'name'),
    createMapping('field_sku', 'sku'),
    createMapping('field_price', 'price'),
    createMapping('status', 'availability'),
    createMapping('field_stock', 'stock_quantity'),
    createMapping('body.value', 'description'),
  ],
  simple_db: [
    createMapping('name', 'name'),
    createMapping('sku', 'sku'),
    createMapping('price', 'price'),
    createMapping('available', 'availability'),
    createMapping('stock', 'stock_quantity'),
    createMapping('description', 'description'),
    createMapping('category', 'category'),
    createMapping('image', 'gallery_images'),
  ],
};
